import { type HttpClient, segment } from '../http';

import {
  type AuthSession,
  type AuthUser,
  type LoginPayload,
  type QrLoginPollResult,
  type QrLoginSession,
  type RefreshResult,
  type RegisterPayload,
} from './auth.types';

/**
 * Sign-up, sign-in and token lifecycle.
 *
 * Warning: The refresh token is **always passed explicitly**. The backend also
 * accepts an httpOnly cookie, but this client never relies on it: in the
 * Capacitor WebView the origin is `capacitor://localhost` while the API lives
 * elsewhere, so the cookie would be third-party and dropped.
 *
 * @param http Transport supplied by the application.
 * @returns The `auth` domain of the API client.
 */
export function createAuthApi(http: HttpClient) {
  return {
    /**
     * Creates an account and opens a session.
     *
     * @param payload Credentials and profile fields required at sign-up.
     * @returns The new account with its token pair.
     */
    register: (payload: RegisterPayload) =>
      http.post<AuthSession>('/auth/register', payload),

    /**
     * Opens a session from an e-mail and a password.
     *
     * @returns The account with its token pair.
     */
    login: (payload: LoginPayload) =>
      http.post<AuthSession>('/auth/login', payload),

    /**
     * Signs in at the **administration** door.
     *
     * ⚠️ A different route, not a flag on the ordinary one. The token it
     * returns is refused by every route that is not an administration one, and
     * the token the ordinary door returns is refused by every one that is —
     * so the two applications cannot borrow each other's sessions.
     *
     * @param payload The same credentials the other door takes.
     * @returns The open session.
     * @throws Refuses with `auth.error.admin.forbidden` when the account is not
     * an administrator, its address is unconfirmed, or it is off the platform's
     * own domain. One code for the three, so a refusal tells nothing.
     */
    adminLogin: (payload: LoginPayload) =>
      http.post<AuthSession>('/auth/admin/login', payload),

    /**
     * The signed-in administrator.
     *
     * ⚠️ Its own route, and not `users.getMe()`. That one is an application
     * route, and an administration session is refused there by design — the
     * two applications do not share each other's endpoints any more than they
     * share each other's sessions.
     *
     * @returns The administrator behind the current token.
     */
    adminMe: () => http.get<AuthUser>('/auth/admin/me'),

    /**
     * Renews the administration session from its own cookie.
     *
     * ⚠️ Its own route, because its cookie sits on `/auth/admin` and the browser
     * will not send it anywhere else. That path **is** the separation: an
     * application refresh token never reaches here, and an administration one
     * never reaches `/auth/refresh`. One shared cookie meant the application
     * picked up the administration's token and stored the result.
     *
     * @returns A fresh access token.
     */
    adminRefresh: () =>
      http.post<RefreshResult>('/auth/admin/refresh', {}),

    /**
     * Ends the administration session, and only that one.
     *
     * ⚠️ Its own route for the same reason: signing out of the administration
     * must not end the application session open in the same browser.
     */
    adminLogout: () => http.post<void>('/auth/admin/logout', {}),

    /**
     * Exchanges a refresh token for a fresh pair.
     *
     * Warning: The server rotates the token on every call, so concurrent calls must
     * be de-duplicated by the caller — the second would invalidate the first.
     *
     * @param refreshToken The stored refresh token.
     * @returns The new token pair.
     */
    refresh: (refreshToken: string) =>
      http.post<RefreshResult>('/auth/refresh', { refreshToken }),

    /**
     * Revokes one refresh token, ending that session.
     *
     * @param refreshToken The token to revoke.
     */
    logout: (refreshToken: string) =>
      http.post<void>('/auth/logout', { refreshToken }),

    /**
     * Confirms the address on an account, from the link that was mailed to it.
     *
     * Warning: the token is single-use only in effect, not by design — it is a
     * signed JWT, not a stored record, so a replay just sets `emailVerified`
     * again. Harmless, and the reason a stale link is worth no alarm.
     *
     * @param token The raw token carried by the link.
     */
    verifyEmail: (token: string) =>
      http.post<void>('/auth/verify-email', { token }),

    /**
     * Sends the confirmation message again, to the address on the account.
     *
     * ⚠️ Takes no address. The server reads the one on record — accepting one
     * here would let a signed-in account be pointed at somebody else's inbox.
     */
    resendVerification: () => http.post<void>('/auth/verify-email/resend'),

    /**
     * Disowns an address, deleting the account that was opened with it.
     *
     * ⚠️ Only ever reaches an account whose address is still unconfirmed —
     * one that, by `EmailVerifiedGuard`, has never been able to do anything.
     * The server refuses once it is confirmed, whatever the link says.
     *
     * Warning: called from a click, never on page load. Mail scanners follow
     * URLs in messages, and a deletion that fired on a visit would erase
     * accounts nobody touched.
     *
     * @param token The rejection token carried by the link.
     */
    rejectEmail: (token: string) =>
      http.post<void>('/auth/verify-email/reject', { token }),

    /**
     * Asks for a password reset link to be mailed.
     *
     * ⚠️ Answers 202 whether or not the address exists. That is deliberate on
     * the server — an endpoint that answered differently would let anybody
     * test which addresses hold an account — so the caller must never render
     * "unknown address", and the screen says the same thing either way.
     *
     * @param email The address the reader typed.
     */
    forgotPassword: (email: string) =>
      http.post<void>('/auth/password/forgot', { email }),

    /**
     * Sets a new password from a reset link, and ends every open session.
     *
     * Warning: single-use, and this one really is — the token is stored with a
     * `usedAt`. A reader who reloads the page after succeeding gets a refusal,
     * which is correct and needs to read as "already done", not as a fault.
     *
     * @param token The raw token carried by the link.
     * @param newPassword The chosen password, already checked against the policy.
     */
    resetPassword: (token: string, newPassword: string) =>
      http.post<void>('/auth/password/reset', { token, newPassword }),
  };
}

/**
 * Signing in on one device by scanning a code with an already signed-in one.
 *
 * `createSession` and `pollSession` are called by the screen displaying the
 * code; `confirm` by the device that scans it.
 *
 * @param http Transport supplied by the application.
 * @returns The QR sign-in domain of the API client.
 */
export function createQrLoginApi(http: HttpClient) {
  return {
    /**
     * Opens a pending sign-in session.
     *
     * @returns The session, including the raw token to render as a QR code.
     */
    createSession: () => http.post<QrLoginSession>('/auth/qr-login/session'),

    /**
     * Asks whether the session has been confirmed yet.
     *
     * Warning: The first poll to observe a confirmed session mints the token pair and
     * consumes the session, so a replayed poll can never mint a second one.
     *
     * @param token The raw session token.
     * @returns Either a pending status or the token pair.
     */
    pollSession: (token: string) =>
      http.get<QrLoginPollResult>(`/auth/qr-login/session/${segment(token)}`),

    /**
     * Binds a scanned session to the signed-in account.
     *
     * @param token The raw session token read from the code.
     */
    confirm: (token: string) =>
      http.post<void>('/auth/qr-login/confirm', { token }),
  };
}

export type AuthApi = ReturnType<typeof createAuthApi>;
export type QrLoginApi = ReturnType<typeof createQrLoginApi>;
